import { captureException } from "@sentry/vue";
import { cloneDeep, isNil } from "lodash-es";
import { computed, reactive, toRefs } from "vue";

import VocabService from "@/common/api/vocab.service";
import {
  type Achievement,
  AchievementType,
  type Badge,
  type OpenedAchievement,
  type UnopenedAchievement,
  type UnpackedBadge,
  type UnpackedOpenedBadge
} from "@/common/types/vocab";
import { filterAchievementCollection } from "@/common/vocabulary-trainer-helpers";
import { i18n } from "@/i18n";

const { t } = i18n.global;

const badgeDisplayNameMap: Record<string, string> = {
  BusyBee: t("vocab.achievements.names.BusyBee"),
  MatchingPairsExpert: t("vocab.achievements.names.MatchingPairsExpert"),
  MultipleChoiceExpert: t("vocab.achievements.names.MultipleChoiceExpert"),
  ParticipationAward: t("vocab.achievements.names.ParticipationAward"),
  TrueFalseExpert: t("vocab.achievements.names.TrueFalseExpert"),
  WritingExpert: t("vocab.achievements.names.WritingExpert"),
  DragDropExpert: t("vocab.achievements.names.DragDropExpert"),
  EasyMixExpert: t("vocab.achievements.names.EasyMixExpert"),
  HardMixExpert: t("vocab.achievements.names.HardMixExpert"),

  ExtremePerfectionist: t("vocab.achievements.names.ExtremePerfectionist"),
  Perfectionist: t("vocab.achievements.names.Perfectionist"),

  ChickenDinner: t("vocab.achievements.names.ChickenDinner"),
  BigBrain: t("vocab.achievements.names.BigBrain"),

  Explorer: t("vocab.achievements.names.Explorer"),
  Pioneer: t("vocab.achievements.names.Pioneer"),
  LazyBum: t("vocab.achievements.names.LazyBum"),

  AntiqueCompass: t("vocab.achievements.names.AntiqueCompass"),
  BottleOfRum: t("vocab.achievements.names.BottleOfRum"),
  CaptainSparrow: t("vocab.achievements.names.CaptainSparrow"),
  ChestOfGold: t("vocab.achievements.names.ChestOfGold"),
  GoDigging: t("vocab.achievements.names.GoDigging"),
  MessageInABottle: t("vocab.achievements.names.MessageInABottle"),
  OhCrab: t("vocab.achievements.names.OhCrab"),
  OldAnchor: t("vocab.achievements.names.OldAnchor"),
  PiratesAhoy: t("vocab.achievements.names.PiratesAhoy"),
  XMarksTheSpot: t("vocab.achievements.names.XMarksTheSpot")
};

const getBadgeDisplayName = (badge: Badge): string => {
  const [name, tier] = badge.name.split("-");

  if (!badgeDisplayNameMap[name]) {
    captureException(new Error(`Unknown badge name: ${name}`));
    return t("vocab.achievements.names.Unknown");
  }

  return `${badgeDisplayNameMap[name]} ${tier}`;
};

export const unpackAchievementBadge = (achievement: Achievement): UnpackedBadge => {
  if (isNil(achievement.badge)) {
    throw Error("Badge is null");
  }

  return {
    ...achievement.badge,
    displayName: getBadgeDisplayName(achievement.badge),
    color: achievement.color
  };
};

interface State {
  achievements: Achievement[];
}

const initialState: State = {
  achievements: []
};

export const useVocabAchievements = () => {
  const state = reactive<State>(cloneDeep(initialState));

  const initialize = async () => {
    const data: Achievement[] = (await VocabService.getAchievements()).data.data;
    state.achievements = data;
  };

  const setAchievements = (achievements: Achievement[]) => {
    state.achievements = achievements;
  };

  const badges = computed<UnpackedBadge[]>(() =>
    state.achievements
      .filter((a: Achievement) => !isNil(a.badge))
      .map<UnpackedBadge>(unpackAchievementBadge)
  );

  const openedBadges = computed<UnpackedOpenedBadge[]>(() =>
    badges.value
      .filter((b: UnpackedBadge): b is UnpackedOpenedBadge => !isNil(b.openedAt))
      .sort((a, b) => b.openedAt.localeCompare(a.openedAt))
  );

  const unopenedAchievements = computed<Achievement[]>(() =>
    state.achievements
      .filter(
        (a: Achievement): a is UnopenedAchievement =>
          a.achievedAt !== null && isNil(a.badge?.openedAt)
      )
      .sort((a, b) => {
        // Sort campaign achievements first
        if (a.type === AchievementType.Campaign && b.type !== AchievementType.Campaign) {
          return -1;
        }

        if (b.type === AchievementType.Campaign && a.type !== AchievementType.Campaign) {
          return 1;
        }

        // Sort latest achieved first
        return b.achievedAt.localeCompare(a.achievedAt);
      })
  );

  const ongoingAchievements = computed<Achievement[]>(() => {
    const ongoingAchievements = state.achievements.filter(
      (a: Achievement) => a.achievedAt === null && a.type === AchievementType.Normal
    );

    return filterAchievementCollection(ongoingAchievements);
  });

  const openedAchievements = computed<OpenedAchievement[]>(() =>
    state.achievements.filter(
      (a: Achievement): a is OpenedAchievement => a.achievedAt !== null && !isNil(a.badge?.openedAt)
    )
  );

  const openedNormalAchievements = computed<OpenedAchievement[]>(() =>
    openedAchievements.value.filter((a: OpenedAchievement) => a.type === AchievementType.Normal)
  );

  const openedCampaignAchievements = computed<OpenedAchievement[]>(() =>
    openedAchievements.value
      .filter((a: OpenedAchievement) => a.type === AchievementType.Campaign)
      .sort((a, b) => new Date(b.achievedAt).getTime() - new Date(a.achievedAt).getTime())
  );

  const achievementHasMultipleTiers = (achievement: Achievement): boolean =>
    state.achievements.filter((a: Achievement) => a.title === achievement.title).length > 1;

  return {
    ...toRefs(state),

    openedBadges,
    unopenedAchievements,
    ongoingAchievements,
    openedNormalAchievements,
    openedCampaignAchievements,

    initialize,
    setAchievements,
    achievementHasMultipleTiers
  };
};
